import {
  useQuery,
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions
} from '@tanstack/react-query';

import type { GetAuthorRecipesQuery } from '@schema/cart';

import API from '@api';
import { withSsrCache } from '../../ssrCache';

export const AUTHOR_RECIPES_LIMIT = 5;

/**
 * ЭТАП 2 — автор страницы /author/:name. Вынесено в фабрику (было только
 * хуком): ЕДИНЫЙ источник ключа для компонента и SSR-префетча, ключ обязан
 * совпасть байт-в-байт.
 */
export const authorQueryOptions = (
  params: Parameters<typeof API.getAuthor>[0]
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['author', params],
      queryFn: () => API.getAuthor(params),
      enabled: !!params.identify
    })
  );

export const useAuthorQuery = (params: Parameters<typeof API.getAuthor>[0]) =>
  useQuery(authorQueryOptions(params));

type GetAuthorRecipesDataT = NonNullable<
  GetAuthorRecipesQuery['getRecipesWithFilters']
>;

export type AuthorRecipeItemT = NonNullable<
  GetAuthorRecipesDataT['recipeItems']
>[number];

export const authorRecipesInfiniteQueryOptions = (
  params: Omit<Parameters<typeof API.getAuthorRecipes>[0], 'page'>,
  initialPage = 1
) => {
  const cacheKey = ['authorRecipes', 'infinite', params];

  return withSsrCache(
    infiniteQueryOptions({
      queryKey: cacheKey,
      // Запрос стартует только когда известен identify автора
      enabled: !!params.author,
      queryFn: ({ pageParam }) => {
        const perPage = params.setLimit ?? AUTHOR_RECIPES_LIMIT;

        return API.getAuthorRecipes({ ...params, page: pageParam }).then(
          res => {
            const data = res.getRecipesWithFilters;
            const totalCountPages = Math.ceil(data.totalCount / perPage);

            return {
              items: data.recipeItems || [],
              total: data.totalCount,
              pagination: {
                total: totalCountPages,
                current: pageParam
              },
              sideData: {
                bludoItems: data.bludoItems || []
              }
            };
          }
        );
      },
      initialPageParam: initialPage,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalCount = lastPage.total;
        const limit = params.setLimit ?? AUTHOR_RECIPES_LIMIT;

        return lastPageParam * limit < totalCount
          ? lastPageParam + 1
          : undefined;
      },
      placeholderData: keepPreviousData
    })
  );
};

export type AuthorRecipesInfiniteQueryOptionsT = ReturnType<
  typeof authorRecipesInfiniteQueryOptions
>;
